import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Grid, Typography, Box, Alert, Tabs, Tab } from '@mui/material';
import { PodcastPlayer } from '../podcasts/PodcastPlayer';
import { LoadingSpinner } from '@horwath-htl/shared-ui';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { format } from 'date-fns';
import { MarketPodcast } from '@horwath-htl/shared-types';

interface PodcastsSectionProps {
  marketId: number;
}

export const PodcastsSection: React.FC<PodcastsSectionProps> = ({ marketId }) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [selectedPeriod, setSelectedPeriod] = React.useState<number | null>(null);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['market-podcasts', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/markets/${marketId}/podcasts`);
      
      if (!response) {
        throw new Error('No response from authenticatedFetch');
      }
      
      if (!response.ok) {
        const error: any = new Error(`HTTP ${response.status}`);
        error.response = { status: response.status };
        throw error;
      }
      
      const result = await response.json();
      return result;
    },
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 403) return false;
      return failureCount < 3;
    }
  });

  // Group podcasts by period and get unique periods sorted by most recent first
  const podcastsByPeriod = React.useMemo(() => {
    if (!data?.podcasts) return new Map();
    
    const grouped = new Map<number, MarketPodcast[]>();
    data.podcasts.forEach((podcast: MarketPodcast) => {
      const existing = grouped.get(podcast.reportMonth) || [];
      grouped.set(podcast.reportMonth, [...existing, podcast]);
    });
    
    // Sort by period (most recent first)
    return new Map(Array.from(grouped.entries()).sort((a, b) => b[0] - a[0]));
  }, [data?.podcasts]);

  // Get sorted periods
  const periods = React.useMemo(() => {
    return Array.from(podcastsByPeriod.keys());
  }, [podcastsByPeriod]);

  // Set default selected period to most recent
  React.useEffect(() => {
    if (periods.length > 0 && selectedPeriod === null) {
      setSelectedPeriod(periods[0]);
    }
  }, [periods, selectedPeriod]);

  const formatPeriodLabel = (period: number): string => {
    const year = Math.floor(period / 100);
    const month = period % 100;
    return format(new Date(year, month - 1), 'MMMM yyyy');
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    const isAccessDenied = (error as any)?.response?.status === 403;
    return (
      <Alert severity={isAccessDenied ? "warning" : "error"}>
        {isAccessDenied
          ? "You don't have an active subscription for this market"
          : "Failed to load podcasts"}
      </Alert>
    );
  }

  const podcasts = data?.podcasts || [];
  const marketIdFromResponse = data?.marketId;

  if (podcasts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No podcasts available for this market yet
        </Typography>
      </Box>
    );
  }

  // If only one period, show podcasts directly without tabs
  if (periods.length === 1) {
    return (
      <Grid container spacing={3}>
        {podcasts.map((podcast: any) => (
          <Grid item xs={12} key={podcast.id}>
            <PodcastPlayer podcast={{ ...podcast, marketId: marketIdFromResponse || marketId }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Multiple periods - show tabs
  const currentPeriodPodcasts = selectedPeriod ? podcastsByPeriod.get(selectedPeriod) || [] : [];

  return (
    <Box>
      <Tabs
        value={selectedPeriod}
        onChange={(_, newValue) => setSelectedPeriod(newValue)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {periods.map((period) => (
          <Tab
            key={period}
            label={formatPeriodLabel(period)}
            value={period}
          />
        ))}
      </Tabs>
      
      <Grid container spacing={3}>
        {currentPeriodPodcasts.map((podcast: any) => (
          <Grid item xs={12} key={podcast.id}>
            <PodcastPlayer podcast={{ ...podcast, marketId: marketIdFromResponse || marketId }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};